import moment from "moment";
import { SpaceScope } from "../../passapp-sdk/types/spaceTypes";
import { resolveSpaceId, resolveSpaceScope } from "../../utils/space";
import { IAuthAction } from "../actions/types/authTypes";

export type AuthState = {
  token: string;
  user: { name: string; email: string; facePhoto?: string };
  sessionExpire: boolean;
  country: { country: string; countryName: string; countryCode: string };
  buildings: Array<any>;
  companies: Array<any>;
  professionTypes: Array<any>;
  /** Tipos de vehículo (carrocería) administrados por el admin de edificios. */
  vehicleTypes: Array<any>;
  /** Si el edificio tiene habilitado el módulo de transportistas (globalOptions). */
  allowTransport: boolean;
  /** Textos configurables por edificio (`globalOptions.appTexts`), ya traducidos por el backend
   * según `APP_LANG`. Sin tipar: se consultan por clave con `useAppText`. */
  appTexts: any;
  /** Espacio a cargo del operador de reservas. `null` para el resto de los roles:
   * es lo que decide si el login cae en /reservations. */
  space: SpaceScope | null;
  lots: Array<any>;
  residents: Array<any>;
  employees: Array<any>;
  hikvisions: Array<{ key: string; name: string }>;
  hikvisionsDefault: string[];
  retentionTimeIn: string;
  retentionTimeOut: string;
  loading: boolean;
  invalid: boolean;
  isAuthenticated: boolean;
  lang: string;
  token_file: any;
  token_image: any;
};

const initialState: AuthState = {
  token: "",
  user: { name: "", email: "" },
  sessionExpire: false,
  country: { country: "", countryName: "", countryCode: "" },
  buildings: [],
  companies: [],
  professionTypes: [],
  vehicleTypes: [],
  allowTransport: false,
  appTexts: {},
  space: null,
  lots: [],
  residents: [],
  employees: [],
  hikvisions: [],
  hikvisionsDefault: [],
  retentionTimeIn: "",
  retentionTimeOut: "",
  invalid: false,
  loading: false,
  isAuthenticated: false,
  lang: "es_ES",
  token_file: {
    t: "",
    exp: null,
  },
  token_image: null,
};

/** Del login alcanza con el id del espacio a cargo: el detalle lo completa
 * `GET_SPACE` con lo que arma globalOptions. Si el login ya mandó el objeto
 * entero, se aprovecha en vez de esperar. */
const spaceFromLogin = (space: any): SpaceScope | null =>
  resolveSpaceScope(resolveSpaceId(space), { space });

/** El detalle que no viene no borra el que ya teníamos: globalOptions se vuelve a
 * pedir en cada cambio de idioma y no siempre trae el espacio resuelto. */
const mergeSpace = (current: SpaceScope | null, next: SpaceScope | null): SpaceScope | null => {
  if (!next) return null;
  const previous = current && current.id === next.id ? current : null;
  const labels = next.labels?.length ? next.labels : previous?.labels;

  return {
    id: next.id,
    name: next.name || previous?.name || "",
    labels: labels || [],
  };
};

export const authReducer = (
  state: AuthState = initialState,
  action: IAuthAction<any>,
): AuthState => {
  switch (action.type) {
    case "LOGIN_REQUEST":
      return {
        ...state,
        loading: true,
      };
    case "GET_LOTS":
      return {
        ...state,
        lots: action.payload,
      };
    case "GET_RESIDENTS":
      return {
        ...state,
        residents: action.payload,
      };
    case "GET_EMPLOYEES":
      return {
        ...state,
        employees: action.payload,
      };
    case "GET_PROFESSION_TYPES":
      return {
        ...state,
        professionTypes: action.payload,
      };
    case "GET_VEHICLE_TYPES":
      return {
        ...state,
        vehicleTypes: action.payload,
      };
    case "GET_ALLOW_TRANSPORT":
      return {
        ...state,
        allowTransport: !!action.payload,
      };
    case "GET_APP_TEXTS":
      return {
        ...state,
        appTexts: action.payload || {},
      };
    case "GET_COUNTRY":
      return {
        ...state,
        country: {
          country: action.payload.country,
          countryCode: action.payload.countryCode,
          countryName: action.payload.countryName,
        },
      };
    case "GET_GLOBAL_DEVICE_OPTIONS":
      return {
        ...state,
        hikvisions: action.payload.hikvisions || [],
        hikvisionsDefault: action.payload.hikvisionsDefault || [],
        retentionTimeIn: action.payload.retentionTimeIn || "",
        retentionTimeOut: action.payload.retentionTimeOut || "",
      };
    case "GET_SPACE":
      return {
        ...state,
        space: mergeSpace(state.space, action.payload || null),
      };
    case "LOGIN_FAILURE":
      return {
        ...state,
        isAuthenticated: false,
        loading: false,
      };
    case "LOGIN_SUCCESS":
      return {
        ...state,
        token: action.payload.token,
        country: {
          ...state.country,
          country: action.payload.country,
        },
        buildings: action.payload.buildings,
        companies: action.payload.companies,
        user: action.payload.user,
        // El login solo trae el id; el nombre y las etiquetas llegan con globalOptions.
        space: spaceFromLogin(action.payload.space),
        isAuthenticated: true,
        loading: false,
      };
    case "USER_REQUEST_TOKEN_FILE":
      if (state.token_file?.wait === true) return state;
      return {
        ...state,
        token_file: {
          ...state.token_file,
          wait: true,
        },
      };
    case "USER_TOKEN_FILE":
      return {
        ...state,
        token_file: {
          t: action.payload.token,
          exp: action.payload.exp,
          wait: action.payload.wait,
        },
      };
    case "LOGIN_ERROR":
      return {
        ...state,
        invalid: true,
        loading: false,
      };
    case "RESET_ERROR":
      return {
        ...state,
        invalid: false,
      };
    case "SESSION_EXPIRE":
      return {
        ...state,
        sessionExpire: true,
      };
    case "CHANGE_LANG":
      return {
        ...state,
        lang: action.payload,
      };
    case "LOGOUT":
      return initialState;
    case "TOKEN_IMAGE":
      return {
        ...state,
        token_image: action.payload,
      };
    default:
      return state;
  }
};
